import { cloneTemplate, fillOpening, setCompanyLogo, text } from './dom'
import { companies, openings } from './mock/data'
import { saveOpening, type Workspace, type PreviewActions } from './workspace'

type SearchTab = { id: string; label: string; query: string; draft?: boolean }

export function createSearch(root: HTMLElement, workspace: Workspace, actions: PreviewActions) {
  const page = root.querySelector<HTMLElement>('[data-marketing-page="search"]')!
  const form = page.querySelector<HTMLFormElement>('[data-preview-search-form]')!
  const input = form.elements.namedItem('query') as HTMLInputElement
  const tabs: SearchTab[] = [
    { id: 'all', label: 'All openings', query: '' },
    { id: 'stripe', label: 'Stripe', query: 'Stripe' },
    { id: 'northstar', label: 'Northstar Labs', query: 'Northstar Labs' }
  ]
  let active = 'all'
  let view = 'jobs'
  let company = ''
  let sort = 'newest'
  let nextSearch = 1

  const currentTab = () => tabs.find((tab) => tab.id === active)!
  const results = () => {
    const query = currentTab().query.toLowerCase()
    const list = openings.filter((opening) => `${opening.title} ${opening.company}`.toLowerCase().includes(query))
    if (sort === 'pay') list.sort((a, b) => Number(b.pay.match(/\d+/)?.[0]) - Number(a.pay.match(/\d+/)?.[0]))
    return list
  }

  function newSearch() {
    const id = `search-${nextSearch++}`
    tabs.push({ id, label: 'New search', query: '', draft: true })
    active = id
    render()
    input.focus({ preventScroll: true })
  }

  function saveButton(row: HTMLElement, id: string, label = '+ Save') {
    const button = row.querySelector<HTMLButtonElement>('[data-preview-save-job]')!
    const saved = workspace.jobs.find((job) => job.id === id)
    button.disabled = Boolean(saved)
    button.textContent = saved ? (saved.stage === 'interested' ? '✓ Saved' : '✓ Applied') : label
  }

  function render() {
    const tab = currentTab()
    input.value = tab.query
    page.querySelector('[data-preview-search-tabs]')!.replaceChildren(
      ...tabs.map((search) => {
        const item = cloneTemplate(root, 'search-tab')
        text(item, 'label', search.label)
        const button = item.querySelector<HTMLButtonElement>('[data-preview-search-tab]')!
        button.dataset.previewSearchTab = search.id
        button.setAttribute('aria-pressed', String(search.id === active))
        const close = item.querySelector<HTMLButtonElement>('[data-preview-close-search]')!
        close.dataset.previewCloseSearch = search.id
        close.setAttribute('aria-label', `Close ${search.label} search`)
        close.hidden = search.id === 'all'
        return item
      })
    )
    const missing = tab.query.toLowerCase().includes('northstar')
    page.querySelector<HTMLElement>('[data-preview-search-results]')!.hidden = missing
    page.querySelector<HTMLElement>('[data-preview-search-missing]')!.hidden = !missing
    page.querySelector<HTMLElement>('[data-preview-search-missing-title]')!.textContent =
      `We don’t have ${tab.query} yet.`
    page.querySelector<HTMLElement>('[data-preview-company-submitted]')!.hidden = true
    const list = tab.draft ? [] : results()
    page.querySelector('[data-preview-search-count]')!.textContent = `${list.length} openings`
    page
      .querySelectorAll<HTMLElement>('[data-preview-search-view]')
      .forEach((button) => button.setAttribute('aria-pressed', String(button.dataset.previewSearchView === view)))
    page.querySelector<HTMLElement>('[data-preview-search-cards]')!.hidden = view !== 'jobs'
    page.querySelector<HTMLElement>('[data-preview-search-companies]')!.hidden = view !== 'companies' || !list.length
    page.querySelector<HTMLElement>('[data-preview-search-empty]')!.hidden = list.length > 0
    page.querySelector('[data-preview-search-cards]')!.replaceChildren(
      ...list.map((opening) => {
        const card = cloneTemplate(root, 'job-card')
        fillOpening(root, card, opening)
        saveButton(card, opening.id)
        return card
      })
    )
    const groups = companies.filter((item) => list.some((opening) => opening.company === item.name))
    if (!groups.some((item) => item.name === company)) company = groups[0]?.name ?? ''
    page.querySelector('[data-preview-search-company-list]')!.replaceChildren(
      ...groups.map((item) => {
        const button = cloneTemplate(root, 'company-button')
        button.dataset.previewSelectCompany = item.name
        button.setAttribute('aria-pressed', String(company === item.name))
        setCompanyLogo(root, button.querySelector<HTMLElement>('[data-preview-logo]')!, item.name)
        text(button, 'company', item.name)
        text(button, 'subtitle', item.about)
        text(button, 'count', String(list.filter((opening) => opening.company === item.name).length))
        return button
      })
    )
    page.querySelector('[data-preview-search-company-name]')!.textContent = company
    page.querySelector('[data-preview-search-company-about]')!.textContent =
      companies.find((item) => item.name === company)?.about ?? ''
    page.querySelector('[data-preview-search-follow]')!.textContent = workspace.follows.has(company)
      ? 'Following'
      : '+ Follow'
    page.querySelector('[data-preview-search-company-jobs]')!.replaceChildren(
      ...list
        .filter((opening) => opening.company === company)
        .map((opening) => {
          const row = cloneTemplate(root, 'opening-row')
          fillOpening(root, row, opening)
          saveButton(row, opening.id, '+ Add to board')
          row.querySelector<HTMLElement>('[data-preview-mark-read]')!.hidden = true
          return row
        })
    )
  }

  page.addEventListener('click', (event) => {
    const button = (event.target as Element).closest<HTMLButtonElement>('button')
    if (!button) return
    if (button.hasAttribute('data-preview-new-search')) newSearch()
    if (button.dataset.previewSearchTab) {
      active = button.dataset.previewSearchTab
      render()
    }
    if (button.dataset.previewCloseSearch) {
      const index = tabs.findIndex((tab) => tab.id === button.dataset.previewCloseSearch)
      if (index > 0) {
        tabs.splice(index, 1)
        if (!tabs.some((tab) => tab.id === active)) active = 'all'
        render()
      }
    }
    if (button.dataset.previewSearchView) {
      view = button.dataset.previewSearchView
      render()
    }
    if (button.dataset.previewSelectCompany) {
      company = button.dataset.previewSelectCompany
      render()
    }
    if (button.hasAttribute('data-preview-search-follow')) {
      if (workspace.follows.has(company)) workspace.follows.delete(company)
      else workspace.follows.add(company)
      actions.changed()
    }
    if (button.hasAttribute('data-preview-save-job')) {
      const opening = openings.find(
        (item) => item.id === button.closest<HTMLElement>('[data-opening]')!.dataset.opening
      )!
      saveOpening(workspace, opening)
      actions.changed()
      actions.report(`${opening.company} added to your board.`)
    }
  })
  form.addEventListener('submit', (event) => {
    event.preventDefault()
    const query = input.value.trim()
    if (active === 'all') {
      if (!query) return
      const id = `search-${nextSearch++}`
      tabs.push({ id, label: query, query })
      active = id
    } else Object.assign(currentTab(), { query, label: query || 'New search', draft: !query })
    render()
  })
  page.querySelector('[data-preview-search-sort]')!.addEventListener('change', (event) => {
    sort = (event.target as HTMLSelectElement).value
    render()
  })
  page.querySelector('[data-preview-company-submission]')!.addEventListener('submit', (event) => {
    event.preventDefault()
    page.querySelector<HTMLElement>('[data-preview-company-submitted]')!.hidden = false
    actions.report('Careers page added to this search.')
  })
  return { render }
}
